import {
  Box, Card, CardContent, Typography, Grid, Chip, Button, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Alert, Divider,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { CheckCircle, Cancel, CreditCard, Upgrade } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

const PLAN_FEATURES = {
  starter:    ['1 User', '1 Branch', '500 Products', 'Basic Reports', 'No WhatsApp'],
  business:   ['10 Users', '1 Branch', '5,000 Products', 'Full Reports', 'WhatsApp Enabled'],
  premium:    ['25 Users', '5 Branches', '50,000 Products', 'Full Reports', 'WhatsApp', 'Multi-Branch'],
  enterprise: ['Unlimited Users', 'Unlimited Branches', 'Unlimited Products', 'API Access', 'Priority Support'],
};

const PLAN_COLORS = { starter: 'default', business: 'primary', premium: 'secondary', enterprise: 'warning' };

function PricingCard({ plan, current, onContact }) {
  const isCurrent = current?.slug === plan.slug;
  const features = PLAN_FEATURES[plan.slug] || [];

  return (
    <Card sx={{ height: '100%', border: isCurrent ? '2px solid' : '1px solid', borderColor: isCurrent ? 'primary.main' : 'divider', position: 'relative' }}>
      {isCurrent && <Chip label="Current Plan" color="primary" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }} />}
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography variant="h6" fontWeight={700}>{plan.name}</Typography>
        <Box sx={{ my: 2 }}>
          <Typography variant="h4" fontWeight={800} color="primary.main">
            ₨{fmt(plan.price_monthly)}
            <Typography component="span" variant="body2" color="text.secondary">/mo</Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary">₨{fmt(plan.price_yearly)}/year (save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)</Typography>
        </Box>
        <List dense sx={{ flex: 1 }}>
          {features.map(f => (
            <ListItem key={f} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}><CheckCircle color="success" sx={{ fontSize: 16 }} /></ListItemIcon>
              <ListItemText primary={f} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
        <Button
          fullWidth variant={isCurrent ? 'outlined' : 'contained'} sx={{ mt: 2 }}
          disabled={isCurrent}
          onClick={() => onContact(plan)}
          startIcon={!isCurrent ? <Upgrade /> : null}
        >
          {isCurrent ? 'Active' : plan.price_monthly > (current?.price_monthly || 0) ? 'Upgrade' : 'Downgrade'}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function SubscriptionPage() {
  const { user } = useSelector(s => s.auth);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/plans').catch(() => ({ data: { data: [] } })),
      api.get('/auth/me'),
    ]).then(([plansRes, meRes]) => {
      setPlans(plansRes.data.data || plansRes.data || []);
      const t = meRes.data.data?.tenant;
      setTenantInfo({
        status: t?.status || 'trial',
        trial_ends_at: t?.trial_ends_at || null,
        plan_name: t?.plan_name || 'Trial',
        plan_slug: t?.plan_slug || null,
        price_monthly: t?.price_monthly || 0,
      });
    }).finally(() => setLoading(false));
  }, []);

  const trialDaysLeft = tenantInfo?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(tenantInfo.trial_ends_at) - new Date()) / 86400000))
    : 0;

  const handleContact = (plan) => {
    window.open(`mailto:sales@posapp.pk?subject=Upgrade to ${plan.name} Plan&body=Hi, I would like to upgrade my account (${user?.email}) to the ${plan.name} plan.`);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>Subscription & Billing</Typography>

      {/* Current status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Current Plan</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="h5" fontWeight={700}>{tenantInfo?.plan_name || 'Trial'}</Typography>
                <Chip label={tenantInfo?.status} size="small" color={{ active: 'success', trial: 'info', suspended: 'error', expired: 'default' }[tenantInfo?.status] || 'default'} />
              </Box>
            </Box>
            {tenantInfo?.status === 'trial' && (
              <Box sx={{ flex: 1, maxWidth: 300 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Trial expires</Typography>
                  <Typography variant="body2" fontWeight={600} color={trialDaysLeft <= 3 ? 'error.main' : 'inherit'}>
                    {trialDaysLeft} days left
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={(14 - trialDaysLeft) / 14 * 100} color={trialDaysLeft <= 3 ? 'error' : 'primary'} sx={{ borderRadius: 1 }} />
              </Box>
            )}
          </Box>

          {tenantInfo?.status === 'trial' && trialDaysLeft <= 3 && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              Your trial expires soon! Choose a plan below to continue using POS Pro.
            </Alert>
          )}
          {tenantInfo?.status === 'expired' && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              Your subscription has expired. Please renew to continue.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Pricing plans */}
      <Typography variant="h6" fontWeight={600} mb={2}>Available Plans</Typography>
      {loading ? null : (
        <Grid container spacing={3} mb={4}>
          {plans.map(plan => (
            <Grid item xs={12} sm={6} md={3} key={plan.id}>
              <PricingCard plan={plan} current={tenantInfo?.plan_slug ? { slug: tenantInfo.plan_slug, price_monthly: tenantInfo.price_monthly } : null} onContact={handleContact} />
            </Grid>
          ))}
          {plans.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">Contact <strong>sales@posapp.pk</strong> to subscribe or upgrade your plan.</Alert>
            </Grid>
          )}
        </Grid>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Contact */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={1}>Need Help?</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            To upgrade, downgrade, or pay your invoice — contact our sales team.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<CreditCard />} onClick={() => window.open('mailto:sales@posapp.pk')}>
              Email: sales@posapp.pk
            </Button>
            <Button variant="outlined" onClick={() => window.open('https://wa.me/923001234567')}>
              WhatsApp Support
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
